import React, { useEffect, useRef } from "react";
import {
    CheckCircle2,
    CalendarCheck,
    MessageSquare,
    ShieldCheck,
    Scale,
    Handshake,
} from "lucide-react";

const items = [
    {
        icon: CheckCircle2,
        title: "Saubere Arbeit",
        desc: "Jede Fuge, jede Kante – kontrolliert und exakt.",
        stat: "0 mm",
        statLabel: "Toleranz für Nachlässigkeit",
    },
    {
        icon: CalendarCheck,
        title: "Zuverlässige Termine",
        desc: "Was wir versprechen, halten wir. Auch beim Datum.",
        stat: "98%",
        statLabel: "Termintreue",
    },
    {
        icon: MessageSquare,
        title: "Klare Kommunikation",
        desc: "Ein Ansprechpartner. Direkte Wege. Keine Umwege.",
        stat: "1",
        statLabel: "Ansprechpartner",
    },
    {
        icon: ShieldCheck,
        title: "Solide Ausführung",
        desc: "Baumaterialien und Methoden, die halten.",
        stat: "99%",
        statLabel: "Qualitätsanspruch",
    },
    {
        icon: Scale,
        title: "Faire Offerten",
        desc: "Transparente Kalkulation ohne versteckte Kosten.",
        stat: "0",
        statLabel: "Versteckte Kosten",
    },
    {
        icon: Handshake,
        title: "Persönliche Betreuung",
        desc: "Vom ersten Gespräch bis zur Übergabe – persönlich.",
        stat: "A–Z",
        statLabel: "Betreuung aus einer Hand",
    },
];

function useReveal() {
    const ref = useRef(null);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const io = new IntersectionObserver(
            (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("in-view"); }),
            { threshold: 0.12 }
        );
        el.querySelectorAll(".fade-up").forEach((n) => io.observe(n));
        return () => io.disconnect();
    }, []);
    return ref;
}

export default function WhyUs() {
    const ref = useReveal();
    return (
        <section
            id="warum"
            ref={ref}
            data-testid="whyus-section"
            className="relative bg-[#F5F5F5] py-28 lg:py-40"
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-12">
                <div className="mb-16 lg:mb-24 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
                    <div className="lg:col-span-7">
                        <div className="eyebrow fade-up mb-6">Warum CLER BAU AG</div>
                        <h2
                            className="fade-up text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter text-[#14100D] leading-[1.02]"
                            style={{ fontFamily: "Outfit, sans-serif" }}
                        >
                            Vertrauen entsteht
                            <br />
                            <span className="font-medium">durch Details.</span>
                        </h2>
                    </div>
                    <div className="lg:col-span-4 lg:col-start-9">
                        <p className="fade-up text-[15px] lg:text-[17px] leading-relaxed text-[#14100D]/70 max-w-md">
                            Sechs Prinzipien, die jedes Projekt tragen. Keine Marketing-Sprüche – gelebter Alltag auf unseren Baustellen.
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[rgba(20,16,13,0.12)]">
                    {items.map((it, i) => {
                        const Icon = it.icon;
                        return (
                            <div
                                key={it.title}
                                data-testid={`whyus-item-${i}`}
                                className="fade-up group relative bg-[#FEFEFE] p-8 lg:p-10 min-h-[320px] flex flex-col justify-between overflow-hidden transition-transform duration-500 hover:-translate-y-1"
                                style={{ transitionDelay: `${i * 60}ms` }}
                            >
                                {/* Big background number */}
                                <div
                                    className="absolute -right-2 -bottom-4 text-[130px] leading-none font-light text-[#14100D]/[0.04] pointer-events-none select-none group-hover:text-[#E20514]/[0.08] transition-colors duration-500"
                                    style={{ fontFamily: "Outfit, sans-serif" }}
                                >
                                    {String(i + 1).padStart(2, "0")}
                                </div>

                                {/* Top row: index + icon */}
                                <div className="relative flex items-start justify-between">
                                    <div className="flex items-center gap-3 text-[10px] tracking-[0.28em] uppercase text-[#14100D]/50">
                                        <span>{String(i + 1).padStart(2, "0")}</span>
                                        <span className="h-px w-6 bg-[#14100D]/20" />
                                        <span className="group-hover:text-[#E20514] transition-colors duration-500">
                                            Prinzip
                                        </span>
                                    </div>
                                    <Icon
                                        size={26}
                                        strokeWidth={1.3}
                                        className="text-[#E20514] transition-transform duration-500 group-hover:scale-110"
                                    />
                                </div>

                                {/* Middle: title + desc */}
                                <div className="relative mt-8 lg:mt-10">
                                    <h3
                                        className="text-2xl lg:text-3xl font-medium text-[#14100D] leading-tight"
                                        style={{ fontFamily: "Outfit, sans-serif" }}
                                    >
                                        {it.title}
                                    </h3>
                                    <div className="mt-3 h-px w-8 bg-[#E20514] transition-[width] duration-500 group-hover:w-16" />
                                    <p className="mt-4 text-[15px] leading-relaxed text-[#14100D]/70 max-w-sm">
                                        {it.desc}
                                    </p>
                                </div>

                                {/* Bottom stat */}
                                <div className="relative mt-8 pt-5 border-t border-[rgba(20,16,13,0.08)] flex items-end justify-between gap-4">
                                    <div>
                                        <div
                                            className="text-2xl lg:text-3xl font-medium text-[#14100D]"
                                            style={{ fontFamily: "Outfit, sans-serif" }}
                                        >
                                            {it.stat}
                                        </div>
                                        <div className="mt-1 text-[10px] tracking-[0.22em] uppercase text-[#14100D]/50">
                                            {it.statLabel}
                                        </div>
                                    </div>
                                </div>

                                {/* Hover red bottom line */}
                                <span
                                    className="absolute left-0 right-0 bottom-0 h-[2px] bg-[#E20514] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500"
                                    style={{ transformOrigin: "left" }}
                                />
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
